import type { BuiltInThemeName, ThemeTokens } from "./types";

const WHITE = "#FFFFFF";

/**
 * `light` and `dark` are the widget's own defaults (not Appendix B's palettes) and deliberately
 * near-colourless: pure neutral greys (equal RGB channels) everywhere except
 * the two graphs, which carry GymPlus blue (`prediction_gauge_topring` for the forecast curve,
 * `chart_border` for the week chart's stack). `light` is white with dark-grey text; `dark` is a
 * true dark mode on a near-black ground. Both use the `bold` size table by default. The six presets below
 * them are still the compressed Appendix B palettes.
 */
const LIGHT: ThemeTokens = {
  tab_bg: "#F2F2F2",
  tab_active_bg: "#3A3A3A",
  tab_text: "#5A5A5A",
  tab_active_text: "#FFFFFF",
  realtime_bg: "#FFFFFF",
  availability_border: "#E3E3E3",
  realtime_inner_border: "#ECECEC",
  realtime_small_text: "#4A4A4A",
  realtime_big_text: "#3A3A3A",
  realtime_category_text: "#6A6A6A",
  realtime_total_bg: "#F2F2F2",
  realtime_total_text: "#3A3A3A",
  prediction_bg: "#F2F2F2",
  prediction_border: "#E3E3E3",
  prediction_text: "#4A4A4A",
  prediction_gauge_title: "#6A6A6A",
  prediction_gauge_percentage: "#4A4A4A",
  prediction_gauge: "#E3E3E3",
  prediction_gauge_topring: "#1780B5",
  realtime_powered_by_gymplus: "#9C9C9C",
  weekdayrow_bg: "#3A3A3A",
  weekdayrow_border: "#E3E3E3",
  weekdayrow_text: "#FFFFFF",
  weekdayrow_active_bg: "#FFFFFF",
  weekdayrow_active_text: "#3A3A3A",
  chart_bg: "#FFFFFF",
  chart_border: "#1780B5",
  chart_powered_by_gymplus: "#4A4A4A",
  thickness: "bold",
};

const DARK: ThemeTokens = {
  tab_bg: "#262626",
  tab_active_bg: "#E6E6E6",
  tab_text: "#B5B5B5",
  tab_active_text: "#111111",
  realtime_bg: "#0F0F0F",
  availability_border: "#3A3A3A",
  realtime_inner_border: "#2A2A2A",
  realtime_small_text: "#D0D0D0",
  realtime_big_text: "#F5F5F5",
  realtime_category_text: "#A8A8A8",
  realtime_total_bg: "#1E1E1E",
  realtime_total_text: "#F5F5F5",
  prediction_bg: "#4A4A4A",
  prediction_border: "#3A3A3A",
  prediction_text: "#D0D0D0",
  prediction_gauge_title: "#A8A8A8",
  prediction_gauge_percentage: "#C8C8C8",
  prediction_gauge: "#3A3A3A",
  prediction_gauge_topring: "#60C2E8",
  realtime_powered_by_gymplus: "#7A7A7A",
  weekdayrow_bg: "#E6E6E6",
  weekdayrow_border: "#3A3A3A",
  weekdayrow_text: "#111111",
  weekdayrow_active_bg: "#F5F5F5",
  weekdayrow_active_text: "#111111",
  chart_bg: "#0F0F0F",
  chart_border: "#60C2E8",
  chart_powered_by_gymplus: "#C8C8C8",
  thickness: "bold",
};

interface PresetRoles {
  accent: string;
  darkAccent: string;
  lightBg: string;
  predictionBg: string;
  /** Defaults to white — matches cleangreen's own "rest white/near-white" description. */
  poweredByGymplus?: string;
}

/**
 * Reconstructs a preset from Appendix B's compressed "one accent, one dark accent, one light
 * bg" summary, using `cleangreen`'s own key-by-key breakdown (the one compressed preset that
 * still lists which keys get which role, rather than just three bare hex values) as the
 * template for the other five. This is a best-effort port, not a verbatim transcription like
 * light/dark above — Appendix B never lists the exact hex per key for these six themes, only
 * "same key mapping pattern as cleangreen." See theme.README.md for what to double-check
 * against the real PHP source before trusting these pixel-for-pixel.
 */
function buildFromRoles(roles: PresetRoles): ThemeTokens {
  const poweredByGymplus = roles.poweredByGymplus ?? WHITE;
  return {
    tab_bg: roles.lightBg,
    tab_active_bg: roles.accent,
    tab_text: roles.accent,
    tab_active_text: WHITE,
    realtime_bg: WHITE,
    availability_border: roles.accent,
    realtime_inner_border: roles.lightBg,
    realtime_small_text: roles.accent,
    realtime_big_text: roles.darkAccent,
    realtime_category_text: roles.accent,
    realtime_total_bg: roles.accent,
    realtime_total_text: WHITE,
    prediction_bg: roles.predictionBg,
    prediction_border: roles.accent,
    prediction_text: WHITE,
    prediction_gauge_title: roles.accent,
    prediction_gauge_percentage: roles.darkAccent,
    prediction_gauge: WHITE,
    prediction_gauge_topring: roles.darkAccent,
    realtime_powered_by_gymplus: poweredByGymplus,
    weekdayrow_bg: roles.accent,
    weekdayrow_border: roles.accent,
    weekdayrow_text: WHITE,
    weekdayrow_active_bg: WHITE,
    weekdayrow_active_text: roles.accent,
    chart_bg: WHITE,
    chart_border: roles.accent,
    chart_powered_by_gymplus: roles.accent,
    thickness: "thin",
  };
}

export const BUILT_IN_PRESETS: Record<BuiltInThemeName, ThemeTokens> = {
  light: LIGHT,
  dark: DARK,
  cleangreen: buildFromRoles({
    accent: "#3AAFA9",
    darkAccent: "#2B7A78",
    lightBg: "#FEFFFF",
    predictionBg: "#DEF2F1",
  }),
  coolblue: buildFromRoles({
    accent: "#2d95ec",
    darkAccent: "#1f68a5",
    lightBg: "#eaf4fd",
    predictionBg: "#abd4f7",
    poweredByGymplus: "#6cb4f1",
  }),
  easter: buildFromRoles({
    accent: "#EBB400",
    darkAccent: "#444444",
    lightBg: "#EAEEEE",
    // Not given in Appendix B for this theme — approximated as the light bg value, since
    // that's the closest documented color and predictions gauges elsewhere in the palette
    // family sit on a similarly soft background rather than a saturated one.
    predictionBg: "#EAEEEE",
    poweredByGymplus: "#444444",
  }),
  redhot: buildFromRoles({
    accent: "#c91b25",
    darkAccent: "#221f20",
    // lightBg is the tab bar; the active tab is white, so it must not be near-white itself.
    lightBg: "#F7DDDE",
    predictionBg: "#F7C2C5",
  }),
  graybreeze: buildFromRoles({
    accent: "#8b9898",
    darkAccent: "#212121",
    lightBg: "#EEEFEF",
    predictionBg: "#ebebeb",
  }),
  minimalwarmth: buildFromRoles({
    accent: "#E98074",
    darkAccent: "#E85A4F",
    lightBg: "#EAE7DC",
    predictionBg: "#FFFFFF",
  }),
};
